import React from "react";
import {InputValueDTO, InputValuesFullHistoryDTO, InputValuesHistoryItemDTO} from "../../../models/values";
import {InputVariableDTO} from "../../../models/library";
import {
    Box,
    Button,
    Container,
    Divider,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
} from "@material-ui/core";
import Utils, {API, createApiConfig, DateUtils} from "../../../utils";
import {useTranslation} from "react-i18next";
import {CSVUtils, PivotCSVRow} from "../../../utils/CSV";
import GetAppIcon from "@material-ui/icons/GetApp";
import {compareByAttr} from "../../../utils/compare";
import {useKeycloak} from "@react-keycloak/web";
import {isAdmin} from "../../../utils/auth";
import {DeleteActionButton} from "../../common/buttons";
import {ErrorNotification} from "../../common/notifications";
import {MunicipalityInputVariableDelete} from "../MunicipalityInputVariableDelete";

interface InputValuesSummaryTableProps {
    organizationId: string;
    items: InputValuesHistoryItemDTO[];
    desiredDate: Date;
    onDateChange: (desiredDate: Date) => void;
}

interface InputValuesTableRow {
    sortKey: string;
    municipalityInputVariableId: string;
    inputVariable: InputVariableDTO;
    value: InputValueDTO | null;
}

function item2row(item: InputValuesHistoryItemDTO): InputValuesTableRow {
    return {
        sortKey: item.inputVariable.name.toLowerCase(),
        municipalityInputVariableId: item.municipalityInputVariableId,
        inputVariable: item.inputVariable,
        value: item.value,
    };
}

export const InputValuesSummaryTable: React.FC<InputValuesSummaryTableProps> = (
    props: InputValuesSummaryTableProps
) => {
    //== Init =================================================================
    const [t] = useTranslation(["values"]);
    const {keycloak, initialized} = useKeycloak();
    const [downloading, setDownloading] = React.useState(false);
    const [deletingRow, setDeletingRow] = React.useState<InputValuesTableRow | null>(null);
    const [deleteFailed, setDeleteFailed] = React.useState(false);
    const canDelete = isAdmin(keycloak);
    const rows = props.items.map(item2row).sort(compareByAttr("sortKey", "ascending"));
    //== Handlers =============================================================
    const handleDeleteDone = (): void => {
        setDeletingRow(null);
        props.onDateChange(props.desiredDate);
    };
    const handleDeleteCancel = (): void => {
        setDeletingRow(null);
    };
    const handleDeleteFailed = (): void => {
        setDeletingRow(null);
        setDeleteFailed(true);
    };
    const [localDateStr, setLocalDateStr] = React.useState(DateUtils.dateFormString(props.desiredDate.toISOString()));
    const handleDateChange = (value: string) => {
        setLocalDateStr(value);
    };
    const handleDateKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            const newDate = new Date(localDateStr);
            if (!isNaN(newDate.getTime())) {
                props.onDateChange(newDate);
            }
        }
    };
    const handleExport = async (): Promise<void> => {
        setDownloading(true);
        try {
            const response = await API.get<InputValuesFullHistoryDTO>(
                `/values-input/history/all/${props.organizationId}`,
                createApiConfig(keycloak, initialized)
            );
            const pivotRows: PivotCSVRow[] = response.data.items.map((item) => ({
                label: item.inputVariable.name,
                unit: item.unit?.abbreviation ?? "",
                values: item.values.map((v) => ({gatheredAt: v.gatheredAt, value: v.value})),
            }));
            const csv = CSVUtils.generatePivotCSV(
                pivotRows,
                t("values_input.summary.headers.inputVariable"),
                t("values_input.summary.headers.unit"),
                (date) => t("values_input.summary.headers.valueAt", {date})
            );
            Utils.download("summary.csv", "text/csv;charset=utf-8", [csv]).click();
        } catch (_err) {
            window.alert(t("values_input.summary.downloadError"));
        } finally {
            setDownloading(false);
        }
    };
    //== Render ===============================================================
    return (
        <Container>
            <Box display="flex" alignItems="center" style={{margin: "0.5em"}}>
                <Box flexGrow={1}>
                    <p>{t("values_input.summary.dateIntro")}</p>
                </Box>
                <TextField
                    label={t("values_input.summary.referenceDate")}
                    type="date"
                    variant="outlined"
                    value={localDateStr}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    onChange={(e): void => {
                        handleDateChange(e.target.value);
                    }}
                    onKeyDown={handleDateKeyDown}
                    style={{marginRight: "1em"}}
                />
                <Button onClick={handleExport} variant="contained" startIcon={<GetAppIcon />} disabled={downloading}>
                    {t("values_input.summary.download")}
                </Button>
            </Box>
            {deleteFailed && <ErrorNotification message={t("values_input.summary.notifications.delete_fail")} />}
            <Divider />
            <Table>
                <TableHead>
                    <TableRow>
                        <TableCell>{t("values_input.summary.headers.inputVariable")}</TableCell>
                        <TableCell align="right">{t("values_input.summary.headers.value")}</TableCell>
                        <TableCell align="right">{t("values_input.summary.headers.unit")}</TableCell>
                        <TableCell align="right">{t("values_input.summary.headers.gatheredAt")}</TableCell>
                        {canDelete && <TableCell align="right" />}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((r) => (
                        <TableRow key={r.inputVariable.id}>
                            <TableCell>{r.inputVariable.name}</TableCell>
                            {r.value === null ? (
                                <TableCell colSpan={3} style={{textAlign: "center", fontStyle: "italic"}}>
                                    {t("values_input.summary.noValue")}
                                </TableCell>
                            ) : (
                                <>
                                    <TableCell align="right">{r.value.value}</TableCell>
                                    <TableCell align="right">{r.value.unit?.abbreviation ?? ""}</TableCell>
                                    <TableCell align="right">{DateUtils.dateString(r.value.gatheredAt)}</TableCell>
                                </>
                            )}
                            {canDelete && (
                                <TableCell align="right">
                                    <DeleteActionButton onClick={() => setDeletingRow(r)} />
                                </TableCell>
                            )}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <MunicipalityInputVariableDelete
                entity={
                    deletingRow
                        ? {id: deletingRow.municipalityInputVariableId, name: deletingRow.inputVariable.name}
                        : null
                }
                onDeleted={handleDeleteDone}
                onCancel={handleDeleteCancel}
                onFailed={handleDeleteFailed}
            />
        </Container>
    );
};
